import { useState } from 'react';
import { SceneFlag } from '@generated/components/SceneFlag';
import { PRESETS } from '@generated/components/sceneRange';
import { getSceneScout } from '@generated/server/sceneScout';
import { getStaffPass } from '@generated/hooks/staffPass';
import { SceneGuide } from '@generated/components/SceneGuide';
import type { SceneScout } from '@generated/components/SceneReport';
import { ExternalLink } from 'lucide-react';
import { SCENE_GROUPS } from '@generated/components/sceneVenues';
import { SceneReport } from '@generated/components/SceneReport';

const TABS = [{ id: 'guide', label: 'Print Guide (For the Paper)' }, { id: 'report', label: 'Assignment Scout' }, { id: 'directory', label: 'Scene Directory' }, { id: 'flag', label: 'Scene Flag' }] as const;
type Tab = (typeof TABS)[number]['id'];

function Directory() {
  const count = SCENE_GROUPS.reduce((n, g) => n + g.venues.length, 0);
  return (
    <div className="mt-6">
      <p className="text-sm text-muted-foreground">{count} venues, gardens and spaces. Click any name to open its website.</p>
      <div className="mt-4 grid gap-x-10 gap-y-8 lg:grid-cols-2">
        {SCENE_GROUPS.map((g) => (
          <section key={g.id} className="min-w-0">
            <h3 className="border-b-4 border-double border-foreground pb-1 text-xs font-bold uppercase tracking-[0.18em]">{g.title}</h3>
            <ul>
              {g.venues.map((v) => (
                <li key={v.url} className="border-b border-border py-2.5">
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="group inline-flex min-h-6 items-baseline gap-1 font-[family-name:var(--font-heading)] text-lg font-semibold leading-snug hover:underline underline-offset-4">
                    {v.name}<ExternalLink className="h-3.5 w-3.5 shrink-0 self-center opacity-50 group-hover:opacity-100" />
                  </a>
                  <p className="text-sm"><span className="font-semibold">{v.where}</span>{v.note && <span className="text-muted-foreground"> · {v.note}</span>}</p>
                  <p className="truncate text-xs text-muted-foreground">{v.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

/** Staff section: reader print guide + assignment scout + venue directory. */
export function TheScene() {
  const [tab, setTab] = useState<Tab>('guide');
  // One scout run feeds both the reader guide and the staff assignment sheet.
  const [s, setS] = useState<SceneScout | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<{ from: string; to: string }>(() => PRESETS[0]!.range());
  const run = () => {
    setBusy(true); setError(null);
    getSceneScout({ data: { pass: getStaffPass(), from: range.from, to: range.to } })
      .then((r) => setS(r ?? null))
      .catch((e: unknown) => { console.error(e); setError('The venue websites could not be checked right now. Please try again in a minute.'); })
      .finally(() => setBusy(false));
  };
  const props = { s, busy, error, run, range, setRange };
  return (
    <section>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Downtown · East Village · Loisaida</div>
      <h2 className="mt-1 font-[family-name:var(--font-heading)] text-4xl font-semibold tracking-tight">The Scene</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Underground music, avant-garde literary, radical theater, community gardens and DIY spaces for <em>The Village Star-Revue</em>.</p>
      <div role="tablist" aria-label="The Scene" className="mt-5 flex gap-1 overflow-x-auto border-b-2 border-foreground">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} className={`min-h-11 shrink-0 px-4 text-xs font-bold uppercase tracking-wider ${tab === t.id ? 'bg-foreground text-background' : 'hover:bg-muted'}`}>{t.label}</button>
        ))}
      </div>
      {tab === 'directory' ? <Directory /> : <div className="mt-6">{tab === 'guide' ? <SceneGuide {...props} /> : tab === 'flag' ? <SceneFlag /> : <SceneReport {...props} />}</div>}
    </section>
  );
}
